import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy, Loader2 } from 'lucide-react'
import { API_ROUTES, apiFetch } from '@/config/api-routes'
import { ESPECIALIDADES_SUGERIDAS } from '@/lib/especialidades'
import { PAPEL_LABEL } from '@/lib/types'
import { useAuth } from '@/contexts/AuthContext'

export default function AdminWorkspaceDetailPage() {
  const { id: workspaceId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enterWorkspaceAsAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [busyDados, setBusyDados] = useState(false)
  const [busySenha, setBusySenha] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [workspace, setWorkspace] = useState<Record<string, unknown> | null>(null)
  const [membros, setMembros] = useState<Record<string, unknown>[]>([])
  const [status, setStatus] = useState('ativo')

  const [nomeWorkspace, setNomeWorkspace] = useState('')
  const [titularNome, setTitularNome] = useState('')
  const [titularCrm, setTitularCrm] = useState('')
  const [titularEsp, setTitularEsp] = useState('')
  const [senhaGerada, setSenhaGerada] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)

  useEffect(() => {
    if (!workspaceId) return
    apiFetch(`${API_ROUTES.admin.workspaces}/${workspaceId}`)
      .then(r => r.json())
      .then(j => {
        if (j.error) throw new Error(j.error)
        setWorkspace(j.workspace)
        setMembros(j.membros ?? [])
        setStatus(String(j.workspace?.status ?? 'ativo'))
        setNomeWorkspace(String(j.workspace?.nome ?? ''))
        const t = j.workspace?.titular as { nome?: string; crm?: string; especialidades?: string[] } | null
        setTitularNome(t?.nome ?? '')
        setTitularCrm(t?.crm ?? '')
        setTitularEsp((t?.especialidades ?? []).join(', '))
      })
      .catch(e => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setLoading(false))
  }, [workspaceId])

  async function salvarDados() {
    setBusyDados(true)
    setErro(null)
    try {
      const r = await apiFetch(`${API_ROUTES.admin.workspaces}/${workspaceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nomeWorkspace.trim(),
          titular: {
            nome: titularNome.trim(),
            crm: titularCrm.trim() || null,
            especialidades: titularEsp.split(',').map(s => s.trim()).filter(Boolean),
          },
        }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error ?? 'Falha')
      setWorkspace(prev => prev ? { ...prev, nome: j.workspace.nome } : prev)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao salvar dados')
    } finally {
      setBusyDados(false)
    }
  }

  async function resetarSenha() {
    if (!window.confirm('Gerar uma nova senha inicial para este médico? A senha atual deixa de funcionar.')) return
    setBusySenha(true)
    setErro(null)
    setSenhaGerada(null)
    try {
      const r = await apiFetch(`${API_ROUTES.admin.workspaces}/${workspaceId}/reset-senha`, { method: 'POST' })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error ?? 'Falha')
      setSenhaGerada(j.senha)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao resetar senha')
    } finally {
      setBusySenha(false)
    }
  }

  async function copiarSenha() {
    if (!senhaGerada) return
    await navigator.clipboard.writeText(senhaGerada)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  async function salvarStatus() {
    const statusAtual = String(workspace?.status ?? 'ativo')
    if (status === statusAtual) return
    if (!window.confirm(`Alterar o status do workspace para "${status}"? Isso pode afetar o acesso da equipe.`)) return
    setBusy(true)
    setErro(null)
    try {
      const r = await apiFetch(`${API_ROUTES.admin.workspaces}/${workspaceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error ?? 'Falha')
      setWorkspace(prev => prev ? { ...prev, status: j.workspace.status } : prev)
      navigate('/admin/medicos')
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setBusy(false)
    }
  }

  async function abrir() {
    if (!workspaceId) return
    setBusy(true)
    try {
      await enterWorkspaceAsAdmin(workspaceId)
    } catch {
      setErro('Não foi possível abrir o workspace.')
      setBusy(false)
    }
  }

  const titular = workspace?.titular as { nome?: string; email?: string; crm?: string } | undefined

  if (!workspaceId) return null

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <Link to="/admin/medicos" style={{ fontSize: 13, color: 'var(--primary-deep)', fontWeight: 600 }}>← Voltar</Link>
        <h1 className="page-title" style={{ marginTop: 8 }}>{String(workspace?.nome ?? 'Workspace')}</h1>
        {titular && (
          <p className="page-subtitle" style={{ margin: 0 }}>
            {titular.nome} · {titular.email}{titular.crm ? ` · CRM ${titular.crm}` : ''}
          </p>
        )}
      </header>

      {loading ? (
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: 'var(--muted)' }} aria-hidden />
      ) : (
        <>
          {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

          <div className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Dados do workspace e do médico</div>
            <div>
              <label className="field-label" htmlFor="ws-nome">Nome do workspace</label>
              <input id="ws-nome" className="field-input" value={nomeWorkspace} onChange={e => setNomeWorkspace(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="ws-tit-nome">Nome do médico</label>
              <input id="ws-tit-nome" className="field-input" value={titularNome} onChange={e => setTitularNome(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="field-label" htmlFor="ws-tit-crm">CRM</label>
                <input id="ws-tit-crm" className="field-input" value={titularCrm} onChange={e => setTitularCrm(e.target.value)} placeholder="00000/UF" />
              </div>
              <div>
                <label className="field-label" htmlFor="ws-tit-esp">Especialidades</label>
                <input
                  id="ws-tit-esp" className="field-input" list="ws-esp-sugestoes"
                  value={titularEsp} onChange={e => setTitularEsp(e.target.value)}
                  placeholder="Ginecologia, Mastologia"
                />
                <datalist id="ws-esp-sugestoes">
                  {ESPECIALIDADES_SUGERIDAS.map(esp => <option key={esp} value={esp} />)}
                </datalist>
              </div>
            </div>
            <div>
              <button type="button" className="btn-primary" disabled={busyDados} onClick={salvarDados}>
                {busyDados && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
                Salvar dados
              </button>
            </div>
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Acesso</div>
            <div>
              <button type="button" className="btn-secondary" disabled={busySenha} onClick={resetarSenha}>
                {busySenha && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
                Resetar senha do médico
              </button>
            </div>
            {senhaGerada && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <code style={{ fontSize: 13.5, background: 'var(--bg)', padding: '8px 10px', borderRadius: 8, wordBreak: 'break-all', flex: 1, minWidth: 160 }}>
                  {senhaGerada}
                </code>
                <button type="button" className="btn-secondary" onClick={copiarSenha} style={{ minHeight: 38 }}>
                  {copiado ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
                  {copiado ? 'Copiada' : 'Copiar'}
                </button>
              </div>
            )}
            {senhaGerada && (
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                Essa senha só aparece uma vez — repasse ao médico agora.
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
            <div>
              <label className="field-label" htmlFor="ws-status">Status do workspace</label>
              <select id="ws-status" className="field-input" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="ativo">Ativo</option>
                <option value="suspenso">Suspenso</option>
                <option value="encerrado">Encerrado</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn-primary" disabled={busy} onClick={salvarStatus}>Salvar status</button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                title="Você passará a navegar o app como este workspace, saindo do painel admin"
                onClick={abrir}
              >
                Entrar como este workspace
              </button>
            </div>
          </div>

          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Membros</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {membros.map(m => {
              const perfilRaw = m.perfis_usuario
              const perfil = (Array.isArray(perfilRaw) ? perfilRaw[0] : perfilRaw) as { nome?: string; email?: string } | null
              return (
                <div key={String(m.id)} className="card" style={{ padding: '12px 14px', fontSize: 14 }}>
                  <strong>{PAPEL_LABEL[m.papel as keyof typeof PAPEL_LABEL] ?? String(m.papel)}</strong>
                  {' · '}
                  {String(perfil?.nome ?? m.convite_email ?? '—')}
                  {!m.ativo && ' · inativo'}
                  {m.convite_status === 'pendente' && ' · convite pendente'}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
